// Agent Chat Store - Redux-like state management with useReducer
// Completely decoupled from the main sandbox state

import type {
  AgentState,
  AgentAction,
  AgentSettings,
  ChatMessage,
  AgentRequest,
  PipelineStatus,
  SelectedTemplateInfo,
  V2ToolCall,
} from './types';
import { isSyntheticSpecPath } from '../runner/stripSyntheticSpec';

// Default settings
export const DEFAULT_AGENT_SETTINGS: AgentSettings = {
  model: 'gpt-5-nano',
  temperature: 0.2,
  maxTokens: 4096,
  showRetrievedContext: true,
  autoApplyCode: false,
};

const IDLE_PIPELINE_STATUS: PipelineStatus = {
  step: 'idle',
  message: '',
  modifiedFiles: [],
  completedSteps: [],
};

// Initial state
export const initialAgentState: AgentState = {
  messages: [],
  currentRequest: null,
  isProcessing: false,
  isConnected: false,
  connectionError: null,
  settings: DEFAULT_AGENT_SETTINGS,
  lastRetrievedContext: [],
  checkpoints: [],
  pipelineStatus: { ...IDLE_PIPELINE_STATUS },
  v2Tree: null,
};

// Derive the failed flag for a completed step from its detail payload.
// Returns undefined for steps that don't carry pass/fail signals so the
// renderer can fall back to the step's default icon.
function computeStepFailed(detail: Record<string, unknown> | undefined): boolean | undefined {
  if (!detail) return undefined;
  if (detail.success === false) return true;
  if (detail.success === true) return false;
  // runTests step:end attaches `failed: true` when the structured test
  // result reports `passed: false` — so the chat trail can render the row
  // with a red x without needing to inspect the nested test result.
  if (detail.failed === true) return true;
  if (detail.failed === false) return false;
  const errors = detail.errors;
  if (Array.isArray(errors) && errors.length > 0) return true;
  return undefined;
}

// Generate unique IDs
let messageIdCounter = 0;
let requestIdCounter = 0;
let checkpointIdCounter = 0;

export function generateMessageId(): string {
  return `msg-${Date.now()}-${++messageIdCounter}`;
}

export function generateRequestId(): string {
  return `req-${Date.now()}-${++requestIdCounter}`;
}

export function generateCheckpointId(): string {
  return `cp-${Date.now()}-${++checkpointIdCounter}`;
}

// Reducer function
export function agentReducer(state: AgentState, action: AgentAction): AgentState {
  switch (action.type) {
    case 'SEND_MESSAGE': {
      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: action.payload.content,
        timestamp: Date.now(),
        status: 'complete',
      };
      
      const request: AgentRequest = {
        id: action.payload.requestId,
        query: action.payload.content,
        status: 'pending',
        createdAt: Date.now(),
      };
      
      // Don't create a placeholder assistant message yet — it will be
      // created lazily on the first STREAM_CHUNK so the empty typing
      // cursor doesn't flash while the pipeline is in early stages
      // (retrieving, planning, etc.).
      
      return {
        ...state,
        messages: [...state.messages, userMessage],
        currentRequest: request,
        isProcessing: true,
        pipelineStatus: { step: 'idle', message: 'Starting…', modifiedFiles: [], completedSteps: [] },
      };
    }
    
    case 'MESSAGE_SENT': {
      return state; // No-op, message already added
    }
    
    case 'START_STREAMING': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }
      
      return {
        ...state,
        currentRequest: {
          ...state.currentRequest,
          status: 'generating',
        },
      };
    }
    
    case 'STREAM_CHUNK': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }
      
      // Lazily create the assistant message on the first chunk
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.status === 'streaming') {
        // Already exists — append content
        messages[messages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + action.payload.chunk,
        };
      } else {
        // First chunk — create the assistant message now
        messages.push({
          id: generateMessageId(),
          role: 'assistant',
          content: action.payload.chunk,
          timestamp: Date.now(),
          status: 'streaming',
        });
      }
      
      return {
        ...state,
        messages,
        currentRequest: {
          ...state.currentRequest,
          response: (state.currentRequest.response || '') + action.payload.chunk,
        },
      };
    }
    
    case 'STREAM_COMPLETE': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }
      
      // Persist the pipeline trail into the assistant message so it remains
      // visible after isProcessing turns false and the live indicator hides.
      const finishedSteps = [...(state.pipelineStatus.completedSteps || [])];
      // Push the final step itself (e.g. executing/validating/streaming)
      if (
        state.pipelineStatus.step !== 'idle' &&
        state.pipelineStatus.step !== 'complete' &&
        state.pipelineStatus.step !== 'error'
      ) {
        const finalDetail = state.pipelineStatus.detail;
        finishedSteps.push({
          step: state.pipelineStatus.step,
          message: state.pipelineStatus.message,
          tool: state.pipelineStatus.tool,
          failed: computeStepFailed(finalDetail),
          detail: finalDetail
            ? (typeof finalDetail.summary === 'string'
              ? finalDetail.summary
              : undefined)
            : undefined,
          actions: state.pipelineStatus.step === 'planning' && finalDetail && Array.isArray(finalDetail.actions)
            ? (finalDetail.actions as Array<{ tool: string; reason?: string }>)
            : undefined,
          reasoning: state.pipelineStatus.step === 'planning' && finalDetail && typeof finalDetail.reasoning === 'string'
            ? finalDetail.reasoning as string
            : undefined,
          validationErrors:
            finalDetail && typeof finalDetail === 'object' && Array.isArray(finalDetail.errors)
              ? (finalDetail.errors as unknown[]).filter(
                  (e): e is string => typeof e === 'string',
                )
              : undefined,
          testResults: (() => {
            if (
              state.pipelineStatus.step === 'runTests' &&
              finalDetail &&
              typeof finalDetail === 'object' &&
              (finalDetail as { testResults?: unknown }).testResults &&
              typeof (finalDetail as { testResults: unknown }).testResults === 'object'
            ) {
              return (finalDetail as { testResults: unknown }).testResults as import('./types').RunTestsResult;
            }
            if (
              state.pipelineStatus.step === 'executing' &&
              state.pipelineStatus.tool === 'runTests' &&
              finalDetail &&
              typeof finalDetail === 'object' &&
              finalDetail.result &&
              typeof finalDetail.result === 'object'
            ) {
              return finalDetail.result as unknown as import('./types').RunTestsResult;
            }
            if (
              state.pipelineStatus.step === 'runTests' &&
              state.pipelineStatus.liveTestResults
            ) {
              return state.pipelineStatus.liveTestResults;
            }
            return undefined;
          })(),
          completedAt: Date.now(),
        });
      }
      const finishedFiles = [...state.pipelineStatus.modifiedFiles];

      // Finalize the assistant message (create one if it doesn't exist yet)
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage && lastMessage.role === 'assistant') {
        messages[messages.length - 1] = {
          ...lastMessage,
          status: 'complete',
          codeBlocks: action.payload.codeBlocks,
          pipelineSteps: finishedSteps,
          modifiedFiles: finishedFiles,
        };
      } else {
        // No chunks were received — create a completed message
        messages.push({
          id: generateMessageId(),
          role: 'assistant',
          content: state.currentRequest.response || '',
          timestamp: Date.now(),
          status: 'complete',
          codeBlocks: action.payload.codeBlocks,
          pipelineSteps: finishedSteps,
          modifiedFiles: finishedFiles,
        });
      }
      
      return {
        ...state,
        messages,
        currentRequest: {
          ...state.currentRequest,
          status: 'complete',
          completedAt: Date.now(),
          generatedCode: action.payload.codeBlocks,
          modifiedFiles: state.pipelineStatus.modifiedFiles,
        },
        isProcessing: false,
        pipelineStatus: { step: 'complete', message: 'Done', modifiedFiles: state.pipelineStatus.modifiedFiles, completedSteps: state.pipelineStatus.completedSteps },
      };
    }
    
    case 'REQUEST_ERROR': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }
      
      // Update the last assistant message to show error, or create one
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage && lastMessage.role === 'assistant') {
        messages[messages.length - 1] = {
          ...lastMessage,
          status: 'error',
          error: action.payload.error,
          content: lastMessage.content || action.payload.error,
        };
      } else {
        // No assistant message yet — create one with the error
        messages.push({
          id: generateMessageId(),
          role: 'assistant',
          content: action.payload.error,
          timestamp: Date.now(),
          status: 'error',
          error: action.payload.error,
        });
      }
      
      return {
        ...state,
        messages,
        currentRequest: {
          ...state.currentRequest,
          status: 'error',
          error: action.payload.error,
          completedAt: Date.now(),
        },
        isProcessing: false,
        pipelineStatus: { step: 'error', message: action.payload.error, modifiedFiles: state.pipelineStatus.modifiedFiles, completedSteps: state.pipelineStatus.completedSteps },
      };
    }
    
    case 'CONTEXT_RETRIEVED': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }
      
      return {
        ...state,
        currentRequest: {
          ...state.currentRequest,
          status: 'retrieving',
          retrievedContext: action.payload.context,
        },
        lastRetrievedContext: action.payload.context,
      };
    }

    case 'PIPELINE_STATUS': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }

      const newPipelineStatus = action.payload.status;

      // Map pipeline step to request status when appropriate
      let requestStatus = state.currentRequest.status;
      const stepToRequestStatus: Record<string, AgentRequest['status']> = {
        indexing: 'indexing',
        retrieving: 'retrieving',
        planning: 'planning',
        executing: 'executing',
        validating: 'executing',
        diagnosing: 'executing',
        streaming: 'generating',
      };
      if (stepToRequestStatus[newPipelineStatus.step]) {
        requestStatus = stepToRequestStatus[newPipelineStatus.step]!;
      }

      // Build a detail string from the status payload for the completed step trail
      const buildDetail = (): string | undefined => {
        const d = newPipelineStatus.detail;
        if (!d) return undefined;
        if (typeof d.summary === 'string') return d.summary;
        if (typeof d.success === 'boolean' && typeof d.path === 'string') {
          return d.success ? d.path : 'failed';
        }
        if (Array.isArray(d.errors) && d.errors.length > 0) {
          return (d.errors as string[]).slice(0, 3).join('; ');
        }
        // Duration info from step:end / tool:end / llm:end
        if (typeof d.durationMs === 'number' && d.durationMs > 0) {
          const secs = (d.durationMs as number / 1000).toFixed(1);
          // Include token info if present (from llm:end)
          if (d.tokens && typeof (d.tokens as Record<string, number>).total === 'number') {
            return `${secs}s · ${(d.tokens as Record<string, number>).total} tokens`;
          }
          return `${secs}s`;
        }
        return undefined;
      };

      // When the step changes, push the previous step into completedSteps.
      // Also push when the message changes within the same step (e.g. multiple tool results).
      const completedSteps = [...(state.pipelineStatus.completedSteps || [])];
      const prevStep = state.pipelineStatus.step;
      const prevMessage = state.pipelineStatus.message;
      const stepChanged = prevStep !== newPipelineStatus.step;
      const messageChanged = prevMessage !== newPipelineStatus.message;

      // Heartbeats (long-LLM-call elapsed-time pings, file-streaming
      // ticks, etc.) update the running row's text in-place but MUST
      // NOT push a new completedStep — otherwise the trail fills with
      // dozens of "Still calling gpt-5-nano… (15s)" rows on every
      // long reasoning turn. Backend tags these via
      // `progress.detail === 'llm:heartbeat'`; agentApi forwards the
      // tag through `detail.info`. Any future heartbeat-style event
      // should set the same `info: 'llm:heartbeat'` (or a similarly
      // namespaced sentinel) to opt into suppression.
      const isHeartbeat =
        newPipelineStatus.detail &&
        typeof newPipelineStatus.detail === 'object' &&
        typeof (newPipelineStatus.detail as { info?: unknown }).info === 'string' &&
        ((newPipelineStatus.detail as { info: string }).info).startsWith('llm:heartbeat');

      if (
        !isHeartbeat &&
        prevStep !== 'idle' &&
        prevStep !== 'complete' &&
        prevStep !== 'error' &&
        (stepChanged || messageChanged)
      ) {
        const prevDetail = state.pipelineStatus.detail;
        completedSteps.push({
          step: prevStep,
          message: state.pipelineStatus.message,
          tool: state.pipelineStatus.tool,
          failed: computeStepFailed(prevDetail),
          detail: prevDetail
            ? (typeof prevDetail.summary === 'string'
              ? prevDetail.summary
              : undefined)
            : undefined,
          // Persist planning actions for rich display
          actions: prevStep === 'planning' && prevDetail && Array.isArray(prevDetail.actions)
            ? (prevDetail.actions as Array<{ tool: string; reason?: string }>)
            : undefined,
          reasoning: prevStep === 'planning' && prevDetail && typeof prevDetail.reasoning === 'string'
            ? prevDetail.reasoning as string
            : undefined,
          // Persist full tool call details for expandable inspection
          toolInput: prevDetail && typeof prevDetail === 'object' && prevDetail.args
            ? prevDetail.args as Record<string, unknown>
            : undefined,
          toolResult: prevDetail && typeof prevDetail === 'object' && prevDetail.result
            ? prevDetail.result as Record<string, unknown>
            : undefined,
          fileContentBefore: prevDetail && typeof prevDetail === 'object' && typeof prevDetail.fileContentBefore === 'string'
            ? prevDetail.fileContentBefore as string
            : undefined,
          // Persist selectedTemplates surfaced by the templateSelect step:end
          selectedTemplates: prevDetail && typeof prevDetail === 'object' && Array.isArray(prevDetail.selectedTemplates)
            ? prevDetail.selectedTemplates as SelectedTemplateInfo[]
            : undefined,
          // Persist validation errors for validating / diagnosing steps so
          // the trail can render an expandable per-error list — gives the
          // user a direct view of what the agent is trying to fix instead
          // of a bare "Diagnosing N errors" count.
          validationErrors:
            prevDetail && typeof prevDetail === 'object' && Array.isArray(prevDetail.errors)
              ? (prevDetail.errors as unknown[]).filter(
                  (e): e is string => typeof e === 'string',
                )
              : undefined,
          // Persist runTests output so the trail can render per-test
          // pending/pass/fail rows with expandable assertions.
          //
          // Two source paths:
          //   1. The dedicated `runTests` pipeline node attaches a
          //      structured `testResults` payload to its step:end via
          //      `buildStepEndData`. Use it directly.
          //   2. Legacy fallback — when the executor calls runTests as a
          //      tool, the tool:end's `prevDetail.result.data` carries
          //      the same shape. Kept for backwards compatibility with
          //      any agent topology that still routes through executeNode.
          //   3. Live-streamed snapshot from test:progress events. Used
          //      when the step:end didn't carry an explicit testResults
          //      payload (e.g. older worker not yet redeployed) so the
          //      chat row still has something to expand.
          testResults: (() => {
            if (
              prevStep === 'runTests' &&
              prevDetail &&
              typeof prevDetail === 'object' &&
              (prevDetail as { testResults?: unknown }).testResults &&
              typeof (prevDetail as { testResults: unknown }).testResults === 'object'
            ) {
              return (prevDetail as { testResults: unknown }).testResults as import('./types').RunTestsResult;
            }
            if (
              prevStep === 'executing' &&
              state.pipelineStatus.tool === 'runTests' &&
              prevDetail &&
              typeof prevDetail === 'object' &&
              prevDetail.result &&
              typeof prevDetail.result === 'object'
            ) {
              return prevDetail.result as unknown as import('./types').RunTestsResult;
            }
            if (prevStep === 'runTests' && state.pipelineStatus.liveTestResults) {
              return state.pipelineStatus.liveTestResults;
            }
            return undefined;
          })(),
          // Persist test files the writeTests / editTests step authored
          // (path + full body) so the user can expand the "Writing tests
          // (RED phase) complete" row to see exactly which spec files
          // got created and read each one's assertions inline.
          testWrites:
            (prevStep === 'writeTests' || prevStep === 'editTests') &&
            prevDetail &&
            typeof prevDetail === 'object' &&
            Array.isArray((prevDetail as { testWrites?: unknown }).testWrites)
              ? ((prevDetail as { testWrites: Array<{ path: unknown; content: unknown }> }).testWrites)
                  .filter(
                    (w): w is { path: string; content: string } =>
                      typeof w.path === 'string' && typeof w.content === 'string',
                  )
              : undefined,
          completedAt: Date.now(),
        });
      }

      // For the current status, compute detail so it's available when this step
      // transitions to completed later
      const currentDetail = buildDetail();
      const statusDetail = currentDetail
        ? { ...newPipelineStatus.detail, summary: currentDetail }
        : newPipelineStatus.detail;

      // When the runTests step ends — either finalizing on step:end with a
      // full testResults payload, or transitioning to a different step —
      // the in-flight liveTestResults snapshot has done its job. Clear it
      // so the next runTests cycle starts fresh (otherwise stale rows from
      // a prior pass would bleed into the new live view).
      const runTestsEnded =
        prevStep === 'runTests' && prevStep !== newPipelineStatus.step;
      const nextLiveTestResults = runTestsEnded
        ? undefined
        : state.pipelineStatus.liveTestResults;

      return {
        ...state,
        pipelineStatus: {
          ...state.pipelineStatus,
          ...newPipelineStatus,
          detail: statusDetail,
          completedSteps,
          // Only override modifiedFiles when the payload explicitly provides them;
          // otherwise preserve the accumulated list from FILE_MODIFIED events.
          modifiedFiles: newPipelineStatus.modifiedFiles ?? state.pipelineStatus.modifiedFiles,
          liveTestResults: nextLiveTestResults,
        },
        currentRequest: {
          ...state.currentRequest,
          status: requestStatus,
        },
      };
    }

    case 'TEST_PROGRESS': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }
      const p = action.payload;
      // Merge the streamed event into a live RunTestsResult shape. The
      // structure mirrors the finalized RunTestsResult so existing
      // components (RunTestsResultsList / RunTestsFileRow / RunTestsTestRow)
      // can render the same payload during the run and after it.
      const prev: import('./types').RunTestsResult = state.pipelineStatus.liveTestResults ?? {
        passed: false,
        totalFiles: 0,
        transpileFailures: 0,
        runtimeFailures: 0,
        fileResults: [],
      };
      const fileResults = prev.fileResults.slice();
      let fileIdx = fileResults.findIndex(f => f.path === p.file);
      if (fileIdx < 0) {
        fileResults.push({ path: p.file, tests: [] });
        fileIdx = fileResults.length - 1;
      }
      const file = { ...fileResults[fileIdx]! };
      const tests = (file.tests ?? []).slice();

      const testKey = (suite?: string, name?: string) => `${suite ?? ''}::${name ?? ''}`;
      const incomingKey = testKey(p.suiteName, p.testName);
      const findIdx = () =>
        p.testName
          ? tests.findIndex(t => testKey(undefined, t.name) === testKey(undefined, p.testName) ||
              (t.fullName !== undefined && t.fullName === `${p.suiteName ?? ''} > ${p.testName ?? ''}`) ||
              testKey(t.fullName?.split(' > ')[0], t.name) === incomingKey)
          : -1;

      switch (p.phase) {
        case 'run-start':
          // No test names yet — just remember the totals for the file.
          // We don't pre-populate placeholders since the runtime delivers
          // one `test-start` per test in immediate succession.
          break;
        case 'test-start': {
          const idx = findIdx();
          const row = {
            name: p.testName ?? '',
            fullName: p.suiteName ? `${p.suiteName} > ${p.testName ?? ''}` : p.testName,
            status: 'running' as const,
            passed: false,
            durationMs: undefined,
            failureMessage: undefined,
          };
          if (idx >= 0) tests[idx] = { ...tests[idx]!, ...row };
          else tests.push(row);
          break;
        }
        case 'test-end': {
          const idx = findIdx();
          const status = p.status ?? 'passed';
          const row = {
            name: p.testName ?? '',
            fullName: p.suiteName ? `${p.suiteName} > ${p.testName ?? ''}` : p.testName,
            status,
            passed: status === 'passed',
            durationMs: p.durationMs,
            failureMessage:
              status === 'passed' || status === 'skipped' ? undefined : p.error,
            failureSource:
              status === 'passed' || status === 'skipped' ? undefined : p.failureSource,
          };
          if (idx >= 0) tests[idx] = { ...tests[idx]!, ...row };
          else tests.push(row);
          break;
        }
        case 'run-end':
          // File totals — already implicit in the rows themselves.
          break;
      }

      file.tests = tests;
      fileResults[fileIdx] = file;

      // Recompute roll-ups across all files so the header (RunTestsResultsList
      // summary) always reflects the live state.
      let totalTests = 0;
      let testsPassed = 0;
      let testsFailed = 0;
      for (const fr of fileResults) {
        // Exclude the same synthetic specs (e.g. the boot-smoke probe)
        // stripSyntheticSuites strips from the final result, so this LIVE
        // rollup agrees with the SSE-broadcast count instead of over-counting
        // by the synthetic file.
        if (isSyntheticSpecPath(fr.path)) continue;
        for (const t of fr.tests ?? []) {
          totalTests++;
          if (t.status === 'passed') testsPassed++;
          else if (t.status === 'failed' || t.status === 'error') testsFailed++;
        }
      }

      const liveTestResults: import('./types').RunTestsResult = {
        ...prev,
        fileResults,
        totalFiles: fileResults.length,
        totalTests,
        testsPassed,
        testsFailed,
        passed: testsFailed === 0 && totalTests > 0 && fileResults.every(f =>
          !f.transpileError && !f.runtimeError && (f.tests ?? []).every(t => t.status === 'passed' || t.status === 'skipped'),
        ),
      };

      return {
        ...state,
        pipelineStatus: {
          ...state.pipelineStatus,
          liveTestResults,
        },
      };
    }

    case 'FILE_MODIFIED': {
      if (!state.currentRequest || state.currentRequest.id !== action.payload.requestId) {
        return state;
      }

      const updatedModified = [...state.pipelineStatus.modifiedFiles];
      if (!updatedModified.includes(action.payload.path)) {
        updatedModified.push(action.payload.path);
      }

      return {
        ...state,
        pipelineStatus: {
          ...state.pipelineStatus,
          modifiedFiles: updatedModified,
        },
      };
    }
    
    case 'APPLY_CODE': {
      // Mark code as being applied (handled by side effect)
      return state;
    }
    
    case 'CODE_APPLIED': {
      // Update the code block's applied status and link to checkpoint
      const messages = state.messages.map(msg => {
        if (msg.codeBlocks) {
          const updatedBlocks = msg.codeBlocks.map(block =>
            block.id === action.payload.codeBlockId
              ? { ...block, applied: true, checkpointId: action.payload.checkpointId }
              : block
          );
          return { ...msg, codeBlocks: updatedBlocks };
        }
        return msg;
      });
      
      return { ...state, messages };
    }
    
    case 'CREATE_CHECKPOINT': {
      // Add checkpoint to the beginning of the array (stack - most recent first)
      return {
        ...state,
        checkpoints: [action.payload.checkpoint, ...state.checkpoints],
      };
    }
    
    case 'REVERT_CHECKPOINT': {
      // Mark as being reverted (actual revert handled by side effect)
      return state;
    }
    
    case 'CHECKPOINT_REVERTED': {
      // Mark the checkpoint as reverted and update related code block
      const checkpoints = state.checkpoints.map(cp =>
        cp.id === action.payload.checkpointId
          ? { ...cp, reverted: true }
          : cp
      );
      
      // Find the code block linked to this checkpoint and mark as not applied
      const messages = state.messages.map(msg => {
        if (msg.codeBlocks) {
          const updatedBlocks = msg.codeBlocks.map(block =>
            block.checkpointId === action.payload.checkpointId
              ? { ...block, applied: false, checkpointId: undefined }
              : block
          );
          return { ...msg, codeBlocks: updatedBlocks };
        }
        return msg;
      });
      
      return { ...state, checkpoints, messages };
    }
    
    case 'CLEAR_CHECKPOINTS': {
      return { ...state, checkpoints: [] };
    }
    
    case 'CLEAR_HISTORY': {
      return {
        ...state,
        messages: [],
        currentRequest: null,
        isProcessing: false,
        lastRetrievedContext: [],
        checkpoints: [], // Also clear checkpoints when clearing history
        pipelineStatus: { ...IDLE_PIPELINE_STATUS },
      };
    }
    
    case 'SET_CONNECTED': {
      return {
        ...state,
        isConnected: action.payload,
        connectionError: action.payload ? null : state.connectionError,
      };
    }
    
    case 'SET_CONNECTION_ERROR': {
      return {
        ...state,
        connectionError: action.payload,
        isConnected: action.payload === null,
      };
    }
    
    case 'UPDATE_SETTINGS': {
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
      };
    }
    
    case 'ADD_SYSTEM_MESSAGE': {
      const systemMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'system',
        content: action.payload.content,
        timestamp: Date.now(),
        status: 'complete',
      };

      return {
        ...state,
        messages: [...state.messages, systemMessage],
      };
    }

    // ── v2 recursive-tree pipeline ─────────────────────────────────────
    case 'V2_WORKFLOW_START': {
      return {
        ...state,
        v2Tree: {
          active: true,
          rootId: action.payload.rootId,
          nodes: {},
          focusedId: action.payload.rootId,
          autoFollow: true,
          expandedIds: [action.payload.rootId],
          toolTimeline: [],
          startedAt: Date.now(),
        },
      };
    }

    case 'V2_WORKFLOW_END': {
      if (!state.v2Tree) return state;
      return {
        ...state,
        v2Tree: {
          ...state.v2Tree,
          active: false,
          endedAt: Date.now(),
        },
      };
    }

    case 'V2_NODE_CREATED': {
      // Defensive: if V2_NODE_CREATED arrives before V2_WORKFLOW_START
      // (out-of-order SSE / no-rootId edge cases), auto-initialise the
      // tree with the first node as the implicit root.
      const t = state.v2Tree ?? {
        active: true,
        rootId: action.payload.parentId === null ? action.payload.id : null,
        nodes: {},
        focusedId: null,
        autoFollow: true,
        expandedIds: [],
        toolTimeline: [],
        startedAt: Date.now(),
      };
      const p = action.payload;
      const node = {
        id: p.id,
        parentId: p.parentId,
        depth: p.depth,
        phase: p.phase,
        title: p.title,
        purpose: p.purpose,
        kind: p.kind,
        status: 'pending' as const,
        childIds: [],
      };
      const parent = p.parentId ? t.nodes[p.parentId] : null;
      const nextNodes: typeof t.nodes = {
        ...t.nodes,
        [p.id]: node,
        ...(parent
          ? {
              [parent.id]: {
                ...parent,
                childIds: parent.childIds.includes(p.id)
                  ? parent.childIds
                  : [...parent.childIds, p.id],
              },
            }
          : {}),
      };
      // Auto-expand the parent so the new child is visible in the tree.
      const expandedIds = parent && !t.expandedIds.includes(parent.id)
        ? [...t.expandedIds, parent.id]
        : t.expandedIds;
      return { ...state, v2Tree: { ...t, nodes: nextNodes, expandedIds } };
    }

    case 'V2_NODE_STATUS': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, status, kind } = action.payload;
      const node = t.nodes[id];
      if (!node) return state;
      const next: typeof node = {
        ...node,
        status,
        ...(kind ? { kind } : {}),
        ...(status === 'planning' || status === 'running'
          ? { startedAt: node.startedAt ?? Date.now() }
          : {}),
        ...(status === 'done' || status === 'failed' ? { endedAt: Date.now() } : {}),
      };
      // Auto-follow: focus on the deepest currently-active node, but only
      // if the user hasn't taken over the focus manually.
      const shouldFocus = t.autoFollow && (status === 'planning' || status === 'running');
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: { ...t.nodes, [id]: next },
          focusedId: shouldFocus ? id : t.focusedId,
        },
      };
    }

    case 'V2_NODE_FILE': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, path, contentLength } = action.payload;
      const node = t.nodes[id];
      if (!node) return state;
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: {
            ...t.nodes,
            [id]: { ...node, kind: 'file', filePath: path, fileContentLength: contentLength },
          },
        },
      };
    }

    case 'V2_NODE_ERROR': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, message } = action.payload;
      const node = t.nodes[id];
      if (!node) return state;
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: {
            ...t.nodes,
            [id]: { ...node, status: 'failed', errorMessage: message, endedAt: Date.now() },
          },
        },
      };
    }

    case 'V2_NODE_CHUNK': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, delta, accumulatedLength } = action.payload;
      const node = t.nodes[id];
      if (!node) return state;
      // Cap stored chunk text at 4KB — we only show a tail in the UI
      // and the full content arrives via the eventual node:file event.
      const MAX_BUFFER = 4096;
      const existing = node.liveChunk ?? '';
      const merged = (existing + delta).slice(-MAX_BUFFER);
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: {
            ...t.nodes,
            [id]: { ...node, liveChunk: merged, liveChunkLength: accumulatedLength },
          },
        },
      };
    }

    case 'V2_NODE_ACTIVITY': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, message } = action.payload;
      const node = t.nodes[id];
      if (!node) return state;
      // Keep the latest message + a capped scrollback (newest last). Dedupe an
      // immediate repeat so a chatty step doesn't spam identical lines.
      const log = node.activityLog ?? [];
      const nextLog = log[log.length - 1] === message ? log : [...log, message].slice(-12);
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: { ...t.nodes, [id]: { ...node, activity: message, activityLog: nextLog } },
        },
      };
    }

    case 'V2_NODE_TOOL': {
      const t = state.v2Tree;
      if (!t) return state;
      const { seq, action: toolAction, target, thought, status, detail } = action.payload;
      const call: V2ToolCall = { seq, action: toolAction, target, thought, status, detail };
      const timeline = t.toolTimeline ?? [];
      // Dedupe an exact-duplicate CONSECUTIVE seq (e.g. a re-delivered SSE
      // frame) — same guard shape as V2_NODE_ACTIVITY's immediate-repeat dedupe.
      if (timeline.length > 0 && timeline[timeline.length - 1].seq === seq) return state;
      const nextTimeline = [...timeline, call].slice(-1000);
      return {
        ...state,
        v2Tree: { ...t, toolTimeline: nextTimeline },
      };
    }

    case 'V2_NODE_TOKENS': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, inputTokens, outputTokens, cachedInputTokens, estimated } = action.payload;
      const node = t.nodes[id];
      if (!node) return state; // token event before the node exists — drop it
      // Accumulate: a node (e.g. a fix node) makes several LLM calls.
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: {
            ...t.nodes,
            [id]: {
              ...node,
              inputTokens: (node.inputTokens ?? 0) + inputTokens,
              outputTokens: (node.outputTokens ?? 0) + outputTokens,
              cachedInputTokens: (node.cachedInputTokens ?? 0) + (cachedInputTokens ?? 0),
              tokensEstimated: node.tokensEstimated || estimated,
            },
          },
        },
      };
    }

    case 'V2_NODE_DESIGN_SPEC': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id, spec } = action.payload;
      const node = t.nodes[id];
      if (!node) return state;
      return {
        ...state,
        v2Tree: {
          ...t,
          nodes: {
            ...t.nodes,
            [id]: { ...node, kind: 'designSpec', designSpec: spec },
          },
        },
      };
    }

    case 'V2_UI_FOCUS': {
      const t = state.v2Tree;
      if (!t) return state;
      return {
        ...state,
        // User clicked a node → switch off auto-follow.
        v2Tree: { ...t, focusedId: action.payload.id, autoFollow: false },
      };
    }

    case 'V2_UI_TOGGLE_EXPAND': {
      const t = state.v2Tree;
      if (!t) return state;
      const { id } = action.payload;
      const expandedIds = t.expandedIds.includes(id)
        ? t.expandedIds.filter(x => x !== id)
        : [...t.expandedIds, id];
      return { ...state, v2Tree: { ...t, expandedIds } };
    }

    case 'V2_UI_AUTO_FOLLOW': {
      const t = state.v2Tree;
      if (!t) return state;
      return { ...state, v2Tree: { ...t, autoFollow: action.payload.enabled } };
    }

    default:
      return state;
  }
}
